import * as api from './api';
import {normalize} from 'normalizr';
import {WorkflowStatusSchema} from '../model/WorkflowStatus';
import {ThreadEventSchema} from '../model/ThreadEvent';
import {ThreadSchema} from '../model/Thread';
import {isThreadFileLoading} from './selectors';

export const UPDATE_ENTITIES = 'comm/UPDATE_ENTITIES';

export function updateEntities(entities) {
    return {
        type: UPDATE_ENTITIES,
        payload: entities,
    };
}

export const THREAD_SIDEBAR_TOGGLE = 'comm/THREAD_SIDEBAR_TOGGLE';

export function threadSidebarToggle(state = null) {
    return {
        type: THREAD_SIDEBAR_TOGGLE,
        payload: state,
    };
}

export const THREAD_EVENTS_LOAD = 'comm/THREAD_EVENTS_LOAD';
export const THREAD_EVENTS_LOAD_SUCCESS = 'comm/THREAD_EVENTS_LOAD_SUCCESS';
export const THREAD_EVENTS_LOAD_FAILURE = 'comm/THREAD_EVENTS_LOAD_FAILURE';

export function threadEventsLoad(from, direction = 'next') {
    return async (dispatch, getState) => {
        const state = getState();

        if (state.getIn(['shownThreadEventsMeta', 'loading'])) {
            return;
        }

        dispatch({
            type: THREAD_EVENTS_LOAD,
        });

        const thread = state.get('currentThread');

        try {
            const data = await api.getThreadEvents(thread, from, direction);

            const normalized = normalize(data.data, {
                events: [ThreadEventSchema],
                thread: ThreadSchema,
            });

            dispatch(updateEntities(normalized.entities));
            dispatch(threadEventsLoadSuccess(normalized.result.events, direction, data.meta.hasPrev));
        } catch (e) {
            console.error(e);
            dispatch(threadEventsLoadFailure(e));
        }
    };
}

export function threadEventsLoadSuccess(newEventIds, direction, hasPrev) {
    return {
        type: THREAD_EVENTS_LOAD_SUCCESS,
        payload: {
            events: newEventIds,
            direction,
            hasPrev,
        },
    };
}

export function threadEventsLoadFailure(error) {
    return {
        type: THREAD_EVENTS_LOAD_FAILURE,
        payload: error,
    };
}

export const THREAD_OWNER_UPDATE = 'comm/THREAD_OWNER_UPDATE';
export const THREAD_OWNER_UPDATE_SUCCESS = 'comm/THREAD_OWNER_UPDATE_SUCCESS';
export const THREAD_OWNER_UPDATE_FAILURE = 'comm/THREAD_OWNER_UPDATE_FAILURE';

export function threadOwnerUpdate(newOwnerId) {
    return async (dispatch, getState) => {
        const state = getState();

        if (state.getIn(['threadOwnerMeta', 'loading'])) {
            return;
        }

        dispatch({
            type: THREAD_OWNER_UPDATE,
        });

        const thread = state.get('currentThread');

        try {
            await api.updateThreadOwner(thread, newOwnerId);

            dispatch(threadOwnerUpdateSuccess());
        } catch (e) {
            console.error(e);
            dispatch(threadOwnerUpdateFailure(e));
        }
    };
}

export function threadOwnerUpdateSuccess() {
    return {
        type: THREAD_OWNER_UPDATE_SUCCESS,
    };
}

export function threadOwnerUpdateFailure(error) {
    return {
        type: THREAD_OWNER_UPDATE_FAILURE,
        payload: error,
    };
}

export const THREAD_WORKFLOW_STATUS_UPDATE = 'comm/THREAD_WORKFLOW_STATUS_UPDATE';
export const THREAD_WORKFLOW_STATUS_UPDATE_SUCCESS = 'comm/THREAD_WORKFLOW_STATUS_UPDATE_SUCCESS';
export const THREAD_WORKFLOW_STATUS_UPDATE_FAILURE = 'comm/THREAD_WORKFLOW_STATUS_UPDATE_FAILURE';

export function threadWorkflowStatusUpdate(newWorkflowStatusId) {
    return async (dispatch, getState) => {
        const state = getState();

        if (state.getIn(['threadWorkflowStatusMeta', 'loading'])) {
            return;
        }

        dispatch({
            type: THREAD_WORKFLOW_STATUS_UPDATE,
        });

        const thread = state.get('currentThread');

        try {
            const newWorkflowStatus = await api.updateThreadWorkflowStatus(thread, newWorkflowStatusId);

            const normalized = normalize(newWorkflowStatus, WorkflowStatusSchema);

            dispatch(updateEntities(normalized.entities));
            dispatch(threadWorkflowStatusUpdateSuccess(normalized.result));
        } catch (e) {
            console.error(e);
            dispatch(threadWorkflowStatusUpdateFailure(e));
        }
    };
}

export function threadWorkflowStatusUpdateSuccess(newWorkflowStatusId) {
    return {
        type: THREAD_WORKFLOW_STATUS_UPDATE_SUCCESS,
        payload: newWorkflowStatusId,
    };
}

export function threadWorkflowStatusUpdateFailure(error) {
    return {
        type: THREAD_WORKFLOW_STATUS_UPDATE_FAILURE,
        payload: error,
    };
}

export const THREAD_INPUT = 'comm/THREAD_INPUT';
export const THREAD_INPUT_SUCCESS = 'comm/THREAD_INPUT_SUCCESS';
export const THREAD_INPUT_FAILURE = 'comm/THREAD_INPUT_FAILURE';

export function threadInput(body) {
    return async (dispatch, getState) => {
        const state = getState();

        if (state.getIn(['threadInputMeta', 'loading'])) {
            return;
        }

        dispatch({
            type: THREAD_INPUT,
        });

        const thread = state.get('currentThread');

        try {
            await api.sendMessage(thread, body);

            dispatch(threadInputSuccess());
        } catch (e) {
            dispatch(threadInputFailure(e));
            throw e;
        }
    };
}

export function threadInputSuccess() {
    return {
        type: THREAD_INPUT_SUCCESS,
    };
}

export function threadInputFailure(error) {
    return {
        type: THREAD_INPUT_FAILURE,
        payload: error,
    };
}

export const THREAD_ADD_FILE_UPLOAD = 'comm/THREAD_ADD_FILE_UPLOAD';
export const THREAD_CLEAR_UPLOAD_FILES = 'comm/THREAD_CLEAR_UPLOAD_FILES';
export const THREAD_REMOVE_UPLOAD_FILE = 'comm/THREAD_REMOVE_UPLOAD_FILE';
export const THREAD_FILE_UPLOAD = 'comm/THREAD_FILE_UPLOAD';
export const THREAD_FILE_UPLOAD_UPDATE = 'comm/THREAD_FILE_UPLOAD_UPDATE';
export const THREAD_FILE_UPLOAD_COMPLETE = 'comm/THREAD_FILE_UPLOAD_COMPLETE';

export function threadClearUploadFiles() {
    return {
        type: THREAD_CLEAR_UPLOAD_FILES
    }
}

export function threadRemoveUploadFile(file) {
    return {
        type: THREAD_REMOVE_UPLOAD_FILE,
        payload: {
            file: file
        }
    }
}

export function threadAddUploadFile(file) {
    return {
        type: THREAD_ADD_FILE_UPLOAD,
        payload: {
            file: file
        }
    }
}

export function threadFileUpload() {

    return async (dispatch, getState) => {
        const state = getState();

        if (isThreadFileLoading(state)) {
            return;
        }

        dispatch({
            type: THREAD_FILE_UPLOAD
        });

        const thread = state.get('currentThread');

        const files = state.get('threadUploadedFiles');

        for(let file of files) {
            await api.uploadThreadFile(thread, file, (status, readyState) => dispatch(threadFileUploadUpdate(status, readyState)));
        }
        dispatch(threadClearUploadFiles());
        dispatch(threadFileUploadComplete());
    };
}

export function threadFileUploadUpdate(status, readyState) {
    return {
        type: THREAD_FILE_UPLOAD_UPDATE,
        payload: {
            status,
            readyState,
        }
    }
}

export function threadFileUploadComplete() {
    return {
        type: THREAD_FILE_UPLOAD_COMPLETE,
    }
}

export const THREAD_NOTE_UPDATE = 'comm/THREAD_NOTE_UPDATE';
export const THREAD_NOTE_UPDATE_SUCCESS = 'comm/THREAD_NOTE_UPDATE_SUCCESS';
export const THREAD_NOTE_UPDATE_FAILURE = 'comm/THREAD_NOTE_UPDATE_FAILURE';

export function threadNoteUpdate(newNote) {
    return async (dispatch, getState) => {
        const state = getState();

        if (state.getIn(['threadNoteMeta', 'loading'])) {
            return;
        }

        dispatch({
            type: THREAD_NOTE_UPDATE,
        });

        const thread = state.get('currentThread');

        try {
            await api.updateThreadNote(thread, newNote);

            dispatch(threadNoteUpdateSuccess(newNote))
        } catch (e) {
            console.error(e);
            dispatch(threadNoteUpdateFailure(e));
        }
    };
}

export function threadNoteUpdateSuccess(newNote) {
    return {
        type: THREAD_NOTE_UPDATE_SUCCESS,
        payload: newNote,
    };
}

export function threadNoteUpdateFailure(error) {
    return {
        type: THREAD_NOTE_UPDATE_FAILURE,
        payload: error,
    };
}
